if (!customElements.get('reviews-app')) {
  class ReviewsApp extends HTMLElement {
    #resizeObserver = null;
    #reviewCardElements = [];
    #tabletMediaQuery = window.matchMedia('(min-width: 750px)');
    #desktopMediaQuery = window.matchMedia('(min-width: 990px)');

    connectedCallback() {
      this.listElement = this.querySelector('[data-reviews-list]');
      this.gridElement = this.querySelector('[data-reviews-grid]');
      this.actionsElement = this.querySelector('[data-reviews-actions]');
      this.showAllButtonElement = this.querySelector('[data-reviews-show-all]');
      this.reviewTemplateElement = this.querySelector('[data-review-template]');

      this.handleShowAllClick = this.expandReviewsList.bind(this);
      this.handleBreakpointChange = this.renderReviewColumns.bind(this);
      this.showAllButtonElement?.addEventListener('click', this.handleShowAllClick);
      this.#tabletMediaQuery.addEventListener('change', this.handleBreakpointChange);
      this.#desktopMediaQuery.addEventListener('change', this.handleBreakpointChange);

      this.loadReviews();
    }

    disconnectedCallback() {
      this.showAllButtonElement?.removeEventListener('click', this.handleShowAllClick);
      this.#tabletMediaQuery.removeEventListener('change', this.handleBreakpointChange);
      this.#desktopMediaQuery.removeEventListener('change', this.handleBreakpointChange);
      this.#resizeObserver?.disconnect();
      this.#resizeObserver = null;
    }

    async loadReviews() {
      try {
        const response = await fetch(this.dataset.reviewsUrl);

        if (!response.ok) throw new Error(`Reviews request failed with status ${response.status}`);

        const widgetData = await response.json();
        const testimonials = this.getTestimonials(widgetData);

        if (!testimonials.length) return;

        this.#reviewCardElements = testimonials.map((testimonial) => this.createReviewCard(testimonial));
        this.renderReviewColumns();
        this.listElement.hidden = false;

        this.#resizeObserver = new ResizeObserver(() => this.updateShowAllVisibility());
        this.#resizeObserver.observe(this.gridElement);
      } catch (error) {
        console.error(error);
      }
    }

    getColumnsCount() {
      if (this.#desktopMediaQuery.matches) return 3;
      if (this.#tabletMediaQuery.matches) return 2;

      return 1;
    }

    renderReviewColumns() {
      if (!this.#reviewCardElements.length) return;

      const columnElements = Array.from({ length: this.getColumnsCount() }, () => {
        const columnElement = document.createElement('div');

        columnElement.className = 'reviews-app__column';

        return columnElement;
      });

      this.#reviewCardElements.forEach((reviewCardElement, reviewIndex) => {
        columnElements[reviewIndex % columnElements.length].append(reviewCardElement);
      });

      this.gridElement.replaceChildren(...columnElements);
    }

    getTestimonials(widgetData) {
      const activeWidget = widgetData?.widgets?.find((widget) => widget.enabled && widget.state === 'live');

      return activeWidget?.testimonials?.filter((testimonial) => testimonial.text?.trim()) ?? [];
    }

    createReviewCard(testimonial) {
      const reviewCardFragment = this.reviewTemplateElement.content.cloneNode(true);
      const reviewCardElement = reviewCardFragment.firstElementChild;
      const [authorName] = (testimonial.author || '').split(', ');
      const rating = Math.round(Number(testimonial.rating) || 5);

      reviewCardElement.querySelector('[data-review-text]').textContent = testimonial.text.trim();
      // reviewCardElement.querySelector('[data-review-date]').textContent = testimonial.dateLabel || '';
      reviewCardElement.querySelector('[data-review-name]').textContent = authorName;

      reviewCardElement.querySelectorAll('[data-review-star]').forEach((starElement, starIndex) => {
        starElement.classList.toggle('reviews-app__card-star--empty', starIndex >= rating);
      });

      if (testimonial.pictureUrl) this.renderAvatarImage(reviewCardElement, testimonial.pictureUrl, authorName);

      return reviewCardElement;
    }

    renderAvatarImage(reviewCardElement, pictureUrl, authorName) {
      const avatarElement = reviewCardElement.querySelector('[data-review-avatar]');
      const avatarImageElement = document.createElement('img');

      avatarImageElement.src = pictureUrl;
      avatarImageElement.alt = authorName;
      avatarImageElement.loading = 'lazy';
      avatarImageElement.width = 48;
      avatarImageElement.height = 48;

      avatarElement.classList.add('reviews-app__avatar--image');
      avatarElement.replaceChildren(avatarImageElement);
    }

    updateShowAllVisibility() {
      if (this.listElement.classList.contains('reviews-app__list--expanded')) return;

      const hasHiddenReviews = this.listElement.scrollHeight > this.listElement.clientHeight;

      this.listElement.classList.toggle('reviews-app__list--collapsed', hasHiddenReviews);
      this.actionsElement.hidden = !hasHiddenReviews;
    }

    expandReviewsList() {
      this.#resizeObserver?.disconnect();
      this.listElement.classList.remove('reviews-app__list--collapsed');
      this.listElement.classList.add('reviews-app__list--expanded');
      this.actionsElement.hidden = true;
    }
  }

  customElements.define('reviews-app', ReviewsApp);
}
